import { CSSProperties } from 'react';
import { FormAppearanceSettings } from 'src/shared/store/projects/types';

export type ResolvedTableAppearance = {
  preset: string;
  fontFamily: string;
  fontSize: number;
  textColor: string;
  backgroundColor: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  borderColor: string;
  focusColor: string;
  dropdownBackgroundColor: string;
  dropdownHoverBackgroundColor: string;
  dropdownTextColor: string;
  contextMenuBackgroundColor: string;
  contextMenuHoverBackgroundColor: string;
  customCss: string;
};

const defaultAppearance: ResolvedTableAppearance = {
  preset: 'default',
  fontFamily: 'GothamPro Regular, sans-serif',
  fontSize: 14,
  textColor: '#1E2A5A',
  backgroundColor: '#FFFFFF',
  headerBackgroundColor: '#F6F7FB',
  headerTextColor: '#1E2A5A',
  borderColor: '#EFEFEF',
  focusColor: '#BEA3E5',
  dropdownBackgroundColor: '#271E64',
  dropdownHoverBackgroundColor: '#3D308D',
  dropdownTextColor: '#FFFFFF',
  contextMenuBackgroundColor: '#191054',
  contextMenuHoverBackgroundColor: '#3D308D',
  customCss: '',
};

const presetMap: Record<string, Partial<ResolvedTableAppearance>> = {
  default: {},
  compact: {
    fontSize: 12,
    borderColor: '#E4E8F0',
    headerBackgroundColor: '#F2F4F9',
  },
  contrast: {
    textColor: '#101322',
    backgroundColor: '#FFFFFF',
    headerBackgroundColor: '#101322',
    headerTextColor: '#FFFFFF',
    borderColor: '#CCD3E2',
    focusColor: '#0B74FF',
    dropdownBackgroundColor: '#101322',
    dropdownHoverBackgroundColor: '#0B74FF',
    contextMenuBackgroundColor: '#101322',
    contextMenuHoverBackgroundColor: '#0B74FF',
  },
  custom: {},
};

const normalizeString = (value: string | undefined, fallback: string) =>
  value?.trim() ? value.trim() : fallback;

const normalizeFontSize = (value: number | undefined, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 10 && value <= 32
    ? value
    : fallback;

export const resolveTableAppearance = (
  settings?: FormAppearanceSettings,
): ResolvedTableAppearance => {
  const preset = (settings?.preset || 'default').toLowerCase();
  const presetAppearance = presetMap[preset] || presetMap.default;

  const merged: ResolvedTableAppearance = {
    ...defaultAppearance,
    ...presetAppearance,
    preset,
  };

  return {
    ...merged,
    fontFamily: normalizeString(settings?.fontFamily, merged.fontFamily),
    fontSize: normalizeFontSize(settings?.fontSize, merged.fontSize),
    textColor: normalizeString(settings?.primaryColor, merged.textColor),
    backgroundColor: normalizeString(settings?.backgroundColor, merged.backgroundColor),
    customCss: (settings?.customCss || '').trim(),
  };
};

export const buildAppearanceCssVariables = (
  appearance: ResolvedTableAppearance,
): CSSProperties => {
  const vars = {
    '--table-font-family': appearance.fontFamily,
    '--table-font-size': `${appearance.fontSize}px`,
    '--table-text-color': appearance.textColor,
    '--table-bg-color': appearance.backgroundColor,
    '--table-header-bg-color': appearance.headerBackgroundColor,
    '--table-header-text-color': appearance.headerTextColor,
    '--table-border-color': appearance.borderColor,
    '--table-focus-color': appearance.focusColor,
    '--table-dropdown-bg-color': appearance.dropdownBackgroundColor,
    '--table-dropdown-hover-bg-color': appearance.dropdownHoverBackgroundColor,
    '--table-dropdown-text-color': appearance.dropdownTextColor,
    '--table-context-bg-color': appearance.contextMenuBackgroundColor,
    '--table-context-hover-bg-color': appearance.contextMenuHoverBackgroundColor,
  };

  return vars as CSSProperties;
};

export const buildScopedCustomCss = (customCss: string | undefined, scopeClass: string): string => {
  const normalized = (customCss || '').replace(/<\/style/gi, '').trim();
  if (!normalized) {
    return '';
  }

  if (normalized.includes('{')) {
    return normalized.replace(/(^|})\s*([^@{}][^{}]*)\s*\{/g, (_full, prefix, selectors) => {
      const scopedSelectors = selectors
        .split(',')
        .map((selector: string) => `.${scopeClass} ${selector.trim()}`)
        .join(', ');
      return `${prefix} ${scopedSelectors} {`;
    });
  }

  return `.${scopeClass} { ${normalized} }`;
};
